import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_DOCUMENT_FONT,
  documentFontClass,
  resolveDocumentFont,
  type DocumentFont,
} from "@/lib/document-fonts";
import {
  DEFAULT_SLIDE_ASPECT,
  DEFAULT_SLIDE_THEME,
  customDefaults,
  resolveSlideAspect,
  resolveSlideTheme,
  type CustomSlideTheme,
  type SlideAspect,
  type SlideStyle,
  type SlideTheme,
} from "@/lib/slide-themes";

const THEMES = [
  "default",
  "writer",
  "paper",
  "solarised",
  "forest",
  "ayu",
] as const;

type Theme = (typeof THEMES)[number];
type ThemeMode = "light" | "dark";

/** Neon used to be a separate palette; it is now the Default theme. */
const LEGACY_THEMES: Record<string, Theme> = {
  neon: "default",
};

function isTheme(value: string | null): value is Theme {
  return THEMES.some((theme) => theme === value);
}

function resolveTheme(value: string | null): Theme {
  if (isTheme(value)) return value;
  if (value && value in LEGACY_THEMES) return LEGACY_THEMES[value];
  return "default";
}

export type { DocumentFont, Theme };

/**
 * The slide theme is stored here with the others because this is where
 * appearance preferences live, but it is a genuinely separate axis: it decides
 * what a congregation sees on a screen, not what the author sees while writing.
 * Nothing below reads one from the other.
 */
const SLIDE_THEME_KEY = "slideTheme";
const CUSTOM_SLIDE_THEME_KEY = "slideThemeCustom";
const SLIDE_ASPECT_KEY = "slideAspect";

function readCustomSlideTheme(): CustomSlideTheme {
  try {
    const raw = localStorage.getItem(CUSTOM_SLIDE_THEME_KEY);
    if (!raw) return customDefaults();
    // Merged over the defaults so a palette saved by an older version, missing
    // whatever field was added since, still renders.
    return { ...customDefaults(), ...JSON.parse(raw) };
  } catch {
    return customDefaults();
  }
}

interface ThemeContextType {
  theme: Theme;
  mode: ThemeMode;
  documentFont: DocumentFont;
  /** Which slide theme is selected, including "custom". */
  slideThemeId: string;
  /** The stored custom palette, whether or not it is the selected one. */
  customSlideTheme: CustomSlideTheme;
  /** The selected theme, resolved and ready to render with. */
  slideTheme: SlideTheme;
  /** Which frame shape the deck is built for: "16:9" or "4:3". */
  slideAspectId: string;
  /** The selected aspect, resolved. */
  slideAspect: SlideAspect;
  /**
   * Theme and aspect together — what every renderer and export takes.
   *
   * Handed out as one object so a caller cannot pick up the theme and forget
   * the frame, which would preview a deck in one shape and export it in
   * another.
   */
  slideStyle: SlideStyle;
  setTheme: (theme: Theme) => void;
  setMode: (mode: ThemeMode) => void;
  setDocumentFont: (font: DocumentFont) => void;
  setSlideThemeId: (id: string) => void;
  setCustomSlideTheme: (custom: CustomSlideTheme) => void;
  setSlideAspectId: (id: string) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: "default",
  mode: "light",
  documentFont: DEFAULT_DOCUMENT_FONT,
  slideThemeId: DEFAULT_SLIDE_THEME,
  customSlideTheme: customDefaults(),
  slideTheme: resolveSlideTheme(DEFAULT_SLIDE_THEME),
  slideAspectId: DEFAULT_SLIDE_ASPECT,
  slideAspect: resolveSlideAspect(DEFAULT_SLIDE_ASPECT),
  slideStyle: {
    theme: resolveSlideTheme(DEFAULT_SLIDE_THEME),
    aspect: resolveSlideAspect(DEFAULT_SLIDE_ASPECT),
  },
  setTheme: () => {},
  setMode: () => {},
  setDocumentFont: () => {},
  setSlideThemeId: () => {},
  setCustomSlideTheme: () => {},
  setSlideAspectId: () => {},
  toggleMode: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    return resolveTheme(localStorage.getItem("theme"));
  });

  const [mode, setMode] = useState<ThemeMode>(() => {
    return (localStorage.getItem("mode") as ThemeMode) ||
      (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
  });

  const [documentFont, setDocumentFont] = useState<DocumentFont>(() => {
    return resolveDocumentFont(localStorage.getItem("documentFont"));
  });

  const [slideThemeId, setSlideThemeId] = useState<string>(() => {
    return localStorage.getItem(SLIDE_THEME_KEY) ?? DEFAULT_SLIDE_THEME;
  });

  const [customSlideTheme, setCustomSlideTheme] =
    useState<CustomSlideTheme>(readCustomSlideTheme);

  const [slideAspectId, setSlideAspectId] = useState<string>(() => {
    return localStorage.getItem(SLIDE_ASPECT_KEY) ?? DEFAULT_SLIDE_ASPECT;
  });

  useEffect(() => {
    localStorage.setItem("theme", theme);

    // Remove any existing theme classes (off a snapshot — see the font effect)
    Array.from(document.body.classList)
      .filter((className) => className.startsWith("theme-"))
      .forEach((className) => document.body.classList.remove(className));

    // Add the selected theme class if not default
    if (theme !== "default") {
      document.body.classList.add(`theme-${theme}`);
    }
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("mode", mode);

    if (mode === "dark") {
      document.documentElement.classList.add("dark");
      // Also add dark class to theme-specific elements
      if (theme !== "default") {
        document.body.classList.add("dark");
      }
    } else {
      document.documentElement.classList.remove("dark");
      document.body.classList.remove("dark");
    }
  }, [mode, theme]);

  useEffect(() => {
    localStorage.setItem("documentFont", documentFont);

    // Swap the class rather than diffing against a hand-written list, so a font
    // added to the catalogue needs no change here.
    // Swapped off a snapshot: classList is live, and removing from it mid-
    // iteration skips the next entry.
    const body = document.body;
    Array.from(body.classList)
      .filter((className) => className.startsWith("font-doc-"))
      .forEach((className) => body.classList.remove(className));
    body.classList.add(documentFontClass(documentFont));
  }, [documentFont]);

  useEffect(() => {
    localStorage.setItem(SLIDE_THEME_KEY, slideThemeId);
  }, [slideThemeId]);

  useEffect(() => {
    localStorage.setItem(CUSTOM_SLIDE_THEME_KEY, JSON.stringify(customSlideTheme));
  }, [customSlideTheme]);

  useEffect(() => {
    localStorage.setItem(SLIDE_ASPECT_KEY, slideAspectId);
  }, [slideAspectId]);

  // Nothing here touches the DOM: slide themes are painted onto a canvas and
  // written into exported files, never applied to the app's own chrome.
  //
  // Memoised because these objects are a *render* dependency downstream: every
  // tile in the deck overview repaints its canvas when the style it was handed
  // changes identity, and a fresh object on every provider render would repaint
  // a thirty-slide light table for a change to something else entirely.
  const slideTheme = useMemo(
    () => resolveSlideTheme(slideThemeId, customSlideTheme),
    [slideThemeId, customSlideTheme],
  );
  const slideAspect = useMemo(() => resolveSlideAspect(slideAspectId), [slideAspectId]);
  const slideStyle: SlideStyle = useMemo(
    () => ({ theme: slideTheme, aspect: slideAspect }),
    [slideTheme, slideAspect],
  );

  const toggleMode = () => {
    setMode(prev => prev === "light" ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      mode,
      documentFont,
      slideThemeId,
      customSlideTheme,
      slideTheme,
      slideAspectId,
      slideAspect,
      slideStyle,
      setTheme,
      setMode,
      setDocumentFont,
      setSlideThemeId,
      setCustomSlideTheme,
      setSlideAspectId,
      toggleMode
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
